/**
 * Jira credential + discovery-run fixtures for Settings → Jira
 * Integration mockup mode. Task 137 §D7.
 *
 * Two-credential org: one happy-path connection (succeeded run) and
 * one auth-failed connection so the page can demonstrate every state
 * the live backend can produce.
 *
 * All timestamps derive from FROZEN_NOW.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export interface JiraCredentialFixture {
  id: string;
  siteUrl: string;
  label: string;
  lastRotatedAt: string;
  lastUsedAt: string | null;
  lastFailedAt: string | null;
  lastFailureReason: string | null;
  createdAt: string;
}

export interface JiraDiscoveryRunFixture {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'succeeded' | 'auth_failed' | 'rate_limited' | 'upstream_error' | 'abandoned';
  trigger: 'credential_created' | 'credential_rotated' | 'cron' | 'manual';
  projectsSeen: number;
  projectsArchived: number;
  usersSeen: number;
  usersDeactivated: number;
  linksCreated: number;
  errorDetail: string | null;
}

const HAPPY_ID = 'cred-jira-fgr-prod';
const AUTH_FAILED_ID = 'cred-jira-meridian-staging';

export const JIRA_CREDENTIALS_FIXTURE: readonly JiraCredentialFixture[] = [
  {
    id: HAPPY_ID,
    siteUrl: 'https://example-fgr.atlassian.net',
    label: 'FGR — production',
    createdAt: new Date(FROZEN_NOW_MS - 14 * DAY_MS).toISOString(),
    lastRotatedAt: new Date(FROZEN_NOW_MS - 14 * DAY_MS).toISOString(),
    lastUsedAt: new Date(FROZEN_NOW_MS - 2 * HOUR_MS).toISOString(),
    lastFailedAt: null,
    lastFailureReason: null,
  },
  {
    id: AUTH_FAILED_ID,
    siteUrl: 'https://example-meridian.atlassian.net',
    label: 'Meridian — staging',
    createdAt: new Date(FROZEN_NOW_MS - 7 * DAY_MS).toISOString(),
    lastRotatedAt: new Date(FROZEN_NOW_MS - 3 * DAY_MS).toISOString(),
    lastUsedAt: null,
    lastFailedAt: new Date(FROZEN_NOW_MS - 30 * MINUTE_MS).toISOString(),
    lastFailureReason: 'auth_failed (status=401)',
  },
];

export const JIRA_DISCOVERY_RUNS_FIXTURE: Record<string, readonly JiraDiscoveryRunFixture[]> = {
  [HAPPY_ID]: [
    {
      id: 'run-fgr-1',
      startedAt: new Date(FROZEN_NOW_MS - 2 * HOUR_MS).toISOString(),
      finishedAt: new Date(FROZEN_NOW_MS - 2 * HOUR_MS + 12 * 1000).toISOString(),
      status: 'succeeded',
      trigger: 'cron',
      projectsSeen: 18,
      projectsArchived: 0,
      usersSeen: 47,
      usersDeactivated: 1,
      linksCreated: 12,
      errorDetail: null,
    },
  ],
  [AUTH_FAILED_ID]: [
    {
      id: 'run-meridian-1',
      startedAt: new Date(FROZEN_NOW_MS - 30 * MINUTE_MS).toISOString(),
      finishedAt: new Date(FROZEN_NOW_MS - 30 * MINUTE_MS + 2 * 1000).toISOString(),
      status: 'auth_failed',
      trigger: 'manual',
      projectsSeen: 0,
      projectsArchived: 0,
      usersSeen: 0,
      usersDeactivated: 0,
      linksCreated: 0,
      errorDetail: 'auth_failed (status=401)',
    },
  ],
};

// Task 138 §D7 — worklog status fixture for mockup mode. Shown on
// the Settings → Jira page in the expanded per-credential row. Keyed
// by credential id so the auth-failed credential can show a realistic
// "no worklogs synced yet" state while the happy path shows counts.
export interface JiraWorklogStatusFixture {
  lastRefreshedAt: string | null;
  totalWorklogs: number;
  orphans: number;
  worklogUpdatedCursorMs: string | null;
  worklogDeletedCursorMs: string | null;
}

export const JIRA_WORKLOG_STATUS_FIXTURE: Record<string, JiraWorklogStatusFixture> = {
  [HAPPY_ID]: {
    lastRefreshedAt: new Date(FROZEN_NOW_MS - 5 * MINUTE_MS).toISOString(),
    totalWorklogs: 412,
    orphans: 3,
    worklogUpdatedCursorMs: String(FROZEN_NOW_MS - 5 * MINUTE_MS),
    worklogDeletedCursorMs: String(FROZEN_NOW_MS - 5 * MINUTE_MS),
  },
  [AUTH_FAILED_ID]: {
    lastRefreshedAt: null,
    totalWorklogs: 0,
    orphans: 0,
    worklogUpdatedCursorMs: null,
    worklogDeletedCursorMs: null,
  },
};
